import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar,
  Package,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Truck,
  Loader,
  Box,
  Users,
  Weight,
  Boxes,
  Printer,
  RotateCcw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye
} from 'lucide-react';
import { calculateOrderItemTotal, formatPrice } from '../utils/pricingCalculations';
import {
  selectFilteredOrdersForWeek,
  selectOrdersFilters,
  selectOrdersLoading,
  selectOrdersError,
  setFilters,
  fetchOrdersByWeek,
  createOrder,
  updateOrder,
  deleteOrder,
  revertOrderConfirmation
} from '../store/slices/ordersSlice';
import {
  selectSeason
} from '../store/slices/seasonSlice';
import OrderModal from '../components/Orders/OrderModal';
import OrderDetailModal from '../components/Orders/OrderDetailModal';
import ProductsSummaryCard from '../components/Orders/ProductsSummaryCard';
import DeleteOrderModal from '../components/Orders/DeleteOrderModal';
import CreditModal from '../components/Orders/CreditModal';
import PrintProductsModal from '../components/Orders/PrintProductsModal';
import { Can } from '../components/Auth/Can';
import { useAbility } from '../hooks/useAbility';

const Orders = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ability } = useAbility();
  const ordersFromRedux = useSelector(selectFilteredOrdersForWeek);
  const filters = useSelector(selectOrdersFilters);
  const loading = useSelector(selectOrdersLoading);
  const error = useSelector(selectOrdersError);
  const selectedSeason = useSelector(selectSeason);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [pedidosRecurrentes, setPedidosRecurrentes] = useState([]);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  const { search, status, type, priority, customerId, clientType } = filters;

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="sort-icon-neutral" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp size={14} className="sort-icon-active" /> : 
      <ArrowDown size={14} className="sort-icon-active" />;
  };

  const clientTypeOptions = [
    {
      value: 'all',
      label: 'Todos',
      description: 'Ver todos los pedidos'
    },
    {
      value: 'exportacion',
      label: 'Exportación',
      description: 'USA y Canadá'
    },
    {
      value: 'nacional',
      label: 'Nacional',
      description: 'Chile'
    }
  ];

  const getOrderType = useCallback((order) => {
    // Buscar el cliente en la lista de clientes usando customerId o cliente_id
    const clienteId = order.customerId || order.cliente_id;
    const cliente = clientes.find(c => c.id === clienteId);
    if (!cliente) return 'Nacional';
    
    // Si el país es USA o Canadá, es Exportación
    const pais = cliente.pais?.toUpperCase();
    if (pais === 'USA' || pais === 'CANADA' || pais === 'CANADÁ') {
      return 'Exportación';
    }
    
    // Si es Chile o CL, es Nacional
    return 'Nacional';
  }, [clientes]);

  // Aplicar filtro de clientType localmente usando los clientes cargados y ordenar
  const orders = useMemo(() => {
    // Sumar precio_total guardado en BD para cada pedido
    let ordersWithTotals = ordersFromRedux.map(order => {
      let totalAmount = 0;
      
      if (order.detalles && Array.isArray(order.detalles)) {
        totalAmount = order.detalles.reduce((sum, detalle) => {
          return sum + Number(detalle.precio_total || 0);
        }, 0);
      }
      
      return {
        ...order,
        totalAmount
      };
    });
    
    let filtered = ordersWithTotals;

    // Apply clientType filter
    if (clientType !== 'all' && clientes.length > 0) {
      filtered = ordersWithTotals.filter(order => {
        const clienteId = order.customerId || order.cliente_id;
        const cliente = clientes.find(c => c.id === clienteId);
        
        if (!cliente) return true;
        
        const pais = cliente.pais?.toUpperCase();
        const isExportacion = pais === 'USA' || pais === 'CANADA' || pais === 'CANADÁ';
        
        if (clientType === 'exportacion') {
          return isExportacion;
        } else if (clientType === 'nacional') {
          return !isExportacion;
        }
        
        return true;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'id':
          aValue = a.id || 0;
          bValue = b.id || 0;
          break;
        case 'orderNumber':
          aValue = a.orderNumber?.toLowerCase() || '';
          bValue = b.orderNumber?.toLowerCase() || '';
          break;
        case 'customerName':
          aValue = a.customerName?.toLowerCase() || '';
          bValue = b.customerName?.toLowerCase() || '';
          break;
        case 'deliveryDate':
          aValue = new Date(a.deliveryDate).getTime();
          bValue = new Date(b.deliveryDate).getTime();
          break;
        case 'tipo':
          const tipoA = getOrderType(a);
          const tipoB = getOrderType(b);
          aValue = tipoA.toLowerCase();
          bValue = tipoB.toLowerCase();
          break;
        case 'estado':
          aValue = (a.estado || 'Creado').toLowerCase();
          bValue = (b.estado || 'Creado').toLowerCase();
          break;
        case 'totalAmount':
          aValue = a.totalAmount || 0;
          bValue = b.totalAmount || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      } else {
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [ordersFromRedux, clientType, clientes, sortColumn, sortDirection, productos, getOrderType]);

  // Calcular totales basados en los pedidos filtrados
  const filteredSummary = useMemo(() => {
    const totalOrders = orders.length;
    let totalExportacionUSD = 0;
    let totalNacionalCLP = 0;
    
    // Calcular total de cajas, peso y volumen desde los detalles de los pedidos filtrados
    const productMap = new Map();
    let totalWeight = 0; // Total weight in kg
    let totalVolume = 0; // Total volume in m³
    
    orders.forEach(order => {
      if (order.detalles && Array.isArray(order.detalles)) {
        order.detalles.forEach(detalle => {
          const productoId = detalle.producto_id || detalle.producto?.id;
          const cantidadCajas = detalle.cantidad_cajas || 0;
          const producto = productos.find(p => p.id === productoId);
          
          // Sumar precio_total guardado en BD
          const itemTotal = Number(detalle.precio_total || 0);
          if (order.tipo === 'Exportación') {
            totalExportacionUSD += itemTotal;
          } else if (order.tipo === 'Nacional') {
            totalNacionalCLP += itemTotal;
          }
          
          // Skip Despacho products - they don't have physical boxes
          if (productoId && producto?.category !== 'Despacho') {
            if (productMap.has(productoId)) {
              const existing = productMap.get(productoId);
              productMap.set(productoId, existing + cantidadCajas);
            } else {
              productMap.set(productoId, cantidadCajas);
            }
          }
          
          // Calculate weight: cantidad_cajas * peso_total_caja_kg (skip Despacho)
          if (producto?.peso_total_caja_kg && producto?.category !== 'Despacho') {
            totalWeight += cantidadCajas * parseFloat(producto.peso_total_caja_kg);
          }
          
          // Calculate volume: cantidad_cajas * volumen_m3 (skip Despacho)
          if (producto?.volumen_m3 && producto?.category !== 'Despacho') {
            totalVolume += cantidadCajas * parseFloat(producto.volumen_m3);
          }
        });
      }
    });
    
    const totalCajas = Array.from(productMap.values()).reduce((sum, cajas) => sum + cajas, 0);
    
    return {
      totalOrders,
      totalExportacionUSD,
      totalNacionalCLP,
      totalCajas,
      totalWeight,
      totalVolume
    };
  }, [orders, productos]);

  // Consolidar productos basados en los pedidos filtrados
  const filteredConsolidatedProducts = useMemo(() => {
    const productMap = new Map();

    orders.forEach(order => {
      if (order.detalles && Array.isArray(order.detalles)) {
        order.detalles.forEach(detalle => {
          const productoId = detalle.producto_id || detalle.producto?.id;
          const productoNombre = detalle.producto?.nombre || 'Producto sin nombre';
          const productoCode = detalle.producto?.code || '';
          const cantidadCajas = detalle.cantidad_cajas || 0;
          
          // Calcular el monto usando la utilidad centralizada
          const producto = productos.find(p => p.id === productoId);
          let montoUSD = 0;
          let montoCLP = 0;
          
          // Usar precio_total guardado en BD
          const itemTotal = Number(detalle.precio_total || 0);
          if (order.tipo === 'Exportación') {
            montoUSD = itemTotal;
          } else {
            montoCLP = itemTotal;
          }

          // Skip Despacho products from box count (but keep monetary totals)
          if (productoId) {
            const cajasParaContar = producto?.category === 'Despacho' ? 0 : cantidadCajas;
            
            if (productMap.has(productoId)) {
              const existing = productMap.get(productoId);
              productMap.set(productoId, {
                ...existing,
                totalCajas: existing.totalCajas + cajasParaContar,
                totalMontoUSD: existing.totalMontoUSD + montoUSD,
                totalMontoCLP: existing.totalMontoCLP + montoCLP
              });
            } else {
              productMap.set(productoId, {
                id: productoId,
                nombre: productoNombre,
                code: productoCode,
                totalCajas: cajasParaContar,
                totalMontoUSD: montoUSD,
                totalMontoCLP: montoCLP
              });
            }
          }
        });
      }
    });

    return Array.from(productMap.values()).sort((a, b) => b.totalCajas - a.totalCajas);
  }, [orders, productos]);

  // Cargar clientes, productos y pedidos recurrentes al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        const { clientesAPI, productosAPI, pedidosRecurrentesAPI } = await import('../services/api');
        
        const clientesResponse = await clientesAPI.getAll();
        const productosResponse = await productosAPI.getAll();
        
        setClientes(clientesResponse.data || []);
        setProductos(productosResponse.data || []);
        
        // Cargar pedidos recurrentes si hay temporada seleccionada
        if (selectedSeason) {
          const pedidosRecurrentesResponse = await pedidosRecurrentesAPI.getAll({
            temporada: selectedSeason.year
          });
          setPedidosRecurrentes(pedidosRecurrentesResponse.data || []);
        }
      } catch (err) {
        console.error('Error cargando datos:', err);
        // Mantener arrays vacíos en caso de error
        setClientes([]);
        setProductos([]);
        setPedidosRecurrentes([]);
      }
    };
    
    loadData();
  }, [selectedSeason]);

  useEffect(() => {
    if (!selectedSeason) return;

    const appliedFilters = {
      search,
      status,
      type,
      priority,
      customerId,
      // clientType se aplica localmente, no se envía al backend
    };

    dispatch(
      fetchOrdersByWeek({
        year: selectedSeason.year,
        week: selectedSeason.week,
        filters: appliedFilters,
      })
    );
  }, [dispatch, selectedSeason, search, status, type, priority, customerId]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleCreateOrder = () => {
    setSelectedOrder(null);
    setShowOrderModal(true);
  };


  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setReadOnly(false);
    setShowOrderModal(true);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setReadOnly(true);
    setShowOrderModal(true);
  };

  const handleDeleteOrder = (order) => {
    setSelectedOrder(order);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (orderId) => {
    await dispatch(deleteOrder(orderId));
    // Recargar pedidos
    if (selectedSeason) {
      dispatch(fetchOrdersByWeek({
        year: selectedSeason.year,
        week: selectedSeason.week,
        filters
      }));
    }
  };

  const handleSaveOrder = async (orderData) => {
    try {
      if (selectedOrder) {
        await dispatch(updateOrder({ id: selectedOrder.id, orderData }));
      } else {
        await dispatch(createOrder(orderData));
      }
      setShowOrderModal(false);
      setSelectedOrder(null);
      // Recargar pedidos
      if (selectedSeason) {
        dispatch(fetchOrdersByWeek({
          year: selectedSeason.year,
          week: selectedSeason.week,
          filters
        }));
      }
    } catch (err) {
      console.error('Error al guardar pedido:', err);
    }
  };

  const handleRevertConfirmation = async (order) => {
    if (!window.confirm(`¿Está seguro que desea revertir el pedido ${order.orderNumber} a estado 'Creado'?\n\nEsto permitirá editar el pedido nuevamente.`)) {
      return;
    }

    try {
      await dispatch(revertOrderConfirmation(order.id));
      // Recargar pedidos
      if (selectedSeason) {
        dispatch(fetchOrdersByWeek({
          year: selectedSeason.year,
          week: selectedSeason.week,
          filters
        }));
      }
    } catch (err) {
      console.error('Error al revertir confirmación:', err);
      alert('Error al revertir la confirmación del pedido');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Confirmado':
        return <CheckCircle size={16} />;
      case 'Pendiente':
        return <Clock size={16} />;
      case 'En Producción':
        return <Package size={16} />;
      case 'Programado':
        return <Calendar size={16} />;
      case 'Enviado':
        return <Truck size={16} />;
      case 'Entregado':
        return <CheckCircle size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const getTypeBadgeClass = (type) => {
    if (type === 'Recurrente' || type === 'RECURRENTE') {
      return 'badge badge-info';
    }
    return 'badge badge-secondary';
  };

  const getEstadoBadgeClass = (estado) => {
    switch (estado) {
      case 'Creado':
        return 'badge badge-primary';
      case 'Confirmado':
        return 'badge badge-success';
      case 'Facturado':
        return 'badge badge-secondary';
      default:
        return 'badge badge-secondary';
    }
  };

  return (
    <div className="orders-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('orders.title')}
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            {t('orders.subtitle')}
          </p>
        </div>
        <div className="header-actions">
          {selectedSeason && filteredConsolidatedProducts.length > 0 && (
            <button 
              className="btn btn-primary" 
              onClick={() => setShowPrintModal(true)}
            >
              <Printer size={20} />
              Imprimir
            </button>
          )}
          <Can I="create" this="orders:weekly">
            <button className="btn btn-primary" onClick={handleCreateOrder}>
              <Plus size={20} />
              Nuevo Pedido Semanal
            </button>
          </Can>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="card week-navigation-card">
        <div className="week-navigation">
          <div className="week-info">
            {!selectedSeason && (
              <>
                <h3 className="week-title">Selecciona una temporada y semana</h3>
                <p className="week-dates">
                  Utiliza el selector de temporada en el encabezado para filtrar los pedidos.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="week-summary">
          <h3 className="summary-title">Totales</h3>
          <div className="summary-stats">
            <div className="summary-stat">
              <div className="stat-icon bg-primary">
                <Package size={20} />
              </div>
              <div>
                <p className="stat-label">Pedidos</p>
                <p className="stat-value">{filteredSummary.totalOrders}</p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-secondary">
                <DollarSign size={20} />
              </div>
              <div>
                <p className="stat-label">USD</p>
                <p className="stat-value">{formatPrice(filteredSummary.totalExportacionUSD, 'USD')}</p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-primary">
                <DollarSign size={20} />
              </div>
              <div>
                <p className="stat-label">CLP</p>
                <p className="stat-value">{formatPrice(filteredSummary.totalNacionalCLP, 'CLP')}</p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-accent">
                <Box size={20} />
              </div>
              <div>
                <p className="stat-label">Cajas</p>
                <p className="stat-value">{filteredSummary.totalCajas}</p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-info">
                <Weight size={20} />
              </div>
              <div>
                <p className="stat-label">Peso</p>
                <p className="stat-value">
                  {Math.round(filteredSummary.totalWeight || 0).toLocaleString('es-CL')} <span className="stat-unit">kg</span>
                </p>
              </div>
            </div>

            <div className="summary-stat">
              <div className="stat-icon bg-warning">
                <Boxes size={20} />
              </div>
              <div>
                <p className="stat-label">Volumen</p>
                <p className="stat-value">
                  {(filteredSummary.totalVolume || 0).toLocaleString('es-CL', { 
                    minimumFractionDigits: 3, 
                    maximumFractionDigits: 3 
                  })} <span className="stat-unit">m³</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Client Type Selector */}
      <div className="card client-type-selector-card">
        <div className="client-type-selector-header">
          <Users size={18} />
          <span>Tipo de Cliente</span>
        </div>
        <div className="client-type-selector-buttons">
          {clientTypeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`client-type-button ${filters.clientType === option.value ? 'active' : ''}`}
              onClick={() => handleFilterChange('clientType', option.value)}
            >
              <div className="client-type-button-icon">
                <Users size={18} />
              </div>
              <div className="client-type-button-text">
                <span className="client-type-label">{option.label}</span>
                <span className="client-type-description">{option.description}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p className="text-gray-600">
          {selectedSeason
            ? `Mostrando ${orders.length} pedido${orders.length !== 1 ? 's' : ''} para la semana ${selectedSeason.week} de la temporada ${selectedSeason.year}`
            : 'Selecciona una temporada y semana para visualizar los pedidos correspondientes.'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <div className="alert alert-error">
            <span>Error: {error}</span>
            <button 
              className="btn btn-sm btn-outline"
              onClick={() => {
                const appliedFilters = {
                  search,
                  status,
                  type,
                  priority,
                  customerId,
                };

                if (!selectedSeason) return;
                dispatch(
                  fetchOrdersByWeek({
                    year: selectedSeason.year,
                    week: selectedSeason.week,
                    filters: appliedFilters,
                  })
                );
              }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="card">
        {loading ? (
          <div className="loading-state">
            <Loader className="animate-spin" size={32} />
            <p className="text-gray-600 mt-4">Cargando pedidos...</p>
          </div>
        ) : orders.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th className="th-sortable" onClick={() => handleSort('id')}>
                    <div className="th-content">
                      <span>Pedido</span>
                      {getSortIcon('id')}
                    </div>
                  </th>
                  <th className="th-sortable" onClick={() => handleSort('customerName')}>
                    <div className="th-content">
                      <span>Cliente</span>
                      {getSortIcon('customerName')}
                    </div>
                  </th>
                  <th className="th-sortable" onClick={() => handleSort('deliveryDate')}>
                    <div className="th-content">
                      <span>Entrega</span>
                      {getSortIcon('deliveryDate')}
                    </div>
                  </th>
                  <th className="th-sortable" onClick={() => handleSort('tipo')}>
                    <div className="th-content">
                      <span>Tipo</span>
                      {getSortIcon('tipo')}
                    </div>
                  </th>
                  <th className="th-sortable" onClick={() => handleSort('estado')}>
                    <div className="th-content">
                      <span>Estado</span>
                      {getSortIcon('estado')}
                    </div>
                  </th>
                  <th className="th-sortable" onClick={() => handleSort('totalAmount')}>
                    <div className="th-content">
                      <span>Total</span>
                      {getSortIcon('totalAmount')}
                    </div>
                  </th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <div className="order-info">
                        <div className="order-icon">
                          {getStatusIcon(order.status)}
                        </div>
                        <div>
                          <div className="font-medium">{order.orderNumber}</div>
                          <div className="text-sm text-gray-500">{order.deliveryId}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="customer-info">
                        <div className="font-medium customer-name">{order.customerName}</div>
                        <div className="text-sm text-gray-500">ID: {order.customerId}</div>
                      </div>
                    </td>
                    <td>
                      <div className="delivery-info">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span className="font-medium">
                            {new Date(order.deliveryDate + 'T00:00:00').toLocaleDateString('es-CL')}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={getTypeBadgeClass(getOrderType(order))}>
                        {getOrderType(order)}
                      </span>
                    </td>
                    <td>
                      <span className={getEstadoBadgeClass(order.estado || 'Creado')}>
                        {order.estado || 'Creado'}
                      </span>
                    </td>
                    <td>
                      <div className="price-info">
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-lg">
                            {formatPrice(order.totalAmount, order.tipo === 'Exportación' ? 'USD' : 'CLP')}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className={order.tipo === 'Exportación' ? 'badge badge-primary' : 'badge badge-success'}>
                            {order.tipo === 'Exportación' ? 'USD' : 'CLP'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Can I="credit" this="orders:weekly">
                          <button 
                            className="action-btn credits"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowCreditModal(true);
                            }}
                            title="Créditos"
                          >
                            <DollarSign size={16} />
                          </button>
                        </Can>
                        {order.estado === 'Confirmado' ? (
                          <Can I="read" this="orders:weekly">
                            <button 
                              className="action-btn view"
                              onClick={() => handleViewOrder(order)}
                              title="Ver"
                            >
                              <Eye size={16} />
                            </button>
                          </Can>
                        ) : (
                          <>
                            <Can I="update" this="orders:weekly">
                              <button 
                                className="action-btn edit"
                                onClick={() => handleEditOrder(order)}
                                title="Editar"
                              >
                                <Edit size={16} />
                              </button>
                            </Can>
                            {ability.can('read', 'orders:weekly') && !ability.can('update', 'orders:weekly') && (
                              <button 
                                className="action-btn view"
                                onClick={() => handleViewOrder(order)}
                                title="Ver"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                          </>
                        )}
                        <Can I="revert-confirmation" this="orders:weekly">
                          {order.estado === 'Confirmado' && (
                            <button 
                              className="action-btn revert"
                              onClick={() => handleRevertConfirmation(order)}
                              title="Revertir a Creado"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                        </Can>
                        <Can I="delete" this="orders:weekly">
                          <button 
                            className="action-btn delete"
                            onClick={() => handleDeleteOrder(order)}
                            title="Eliminar"
                            disabled={order.estado === 'Confirmado'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mt-4">
              No hay pedidos para esta semana
            </h3>
            <p className="text-gray-500 mt-2">
              Los pedidos programados para esta semana aparecerán aquí
            </p>
          </div>
        )}
      </div>

      {/* Products Summary */}
      {selectedSeason && orders.length > 0 && (
        <ProductsSummaryCard 
          products={filteredConsolidatedProducts} 
          productos={productos}
          showLimits={clientType === 'exportacion'}
          isExportOnly={clientType === 'exportacion'}
        />
      )}

      {/* Order Modal */}
      <OrderModal
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
          setReadOnly(false);
        }}
        onSave={handleSaveOrder}
        order={selectedOrder}
        clientes={clientes}
        productos={productos}
        temporada={selectedSeason}
        pedidosRecurrentes={pedidosRecurrentes}
        existingOrders={orders}
        readOnly={readOnly}
      />

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        order={selectedOrder}
        productos={productos}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOrder && (
        <DeleteOrderModal
          order={selectedOrder}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedOrder(null);
          }}
          onDelete={handleConfirmDelete}
        />
      )}

      {/* Credit Modal */}
      <CreditModal
        isOpen={showCreditModal}
        onClose={() => {
          setShowCreditModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        productos={productos}
      />

      {/* Print Products Modal */}
      <PrintProductsModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        products={filteredConsolidatedProducts}
        productos={productos}
        clientes={clientes}
        orders={orders}
        temporada={selectedSeason}
        currentClientType={clientType}
      />

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--spacing-6);
        }

        .header-actions {
          display: flex;
          gap: var(--spacing-3);
          align-items: center;
        }

        .client-type-selector-card {
          margin-bottom: var(--spacing-6);
        }

        .client-type-selector-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          margin-bottom: var(--spacing-4);
          font-weight: 600;
          color: var(--gray-700);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .client-type-selector-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: var(--spacing-3);
        }

        .client-type-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
          background: white;
          text-align: left;
          transition: all 0.2s ease-in-out;
          cursor: pointer;
        }

        .client-type-button:hover {
          border-color: var(--primary-blue);
          box-shadow: 0 10px 30px -12px rgba(59, 130, 246, 0.4);
        }

        .client-type-button.active {
          border-color: var(--primary-blue);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02));
        }

        .client-type-button-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background-color: rgba(59, 130, 246, 0.12);
          color: var(--primary-blue);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .client-type-button.active .client-type-button-icon {
          background-color: var(--primary-blue);
          color: white;
        }

        .client-type-button-text {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }

        .client-type-label {
          font-weight: 600;
          color: var(--gray-900);
          font-size: var(--font-size-lg);
        }

        .client-type-description {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }
        
        .week-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-6);
        }
        
        .week-controls {
          display: flex;
          align-items: center;
          gap: var(--spacing-4);
        }
        
        .week-nav-btn {
          padding: var(--spacing-2);
          border: 2px solid var(--gray-300);
          border-radius: var(--radius-md);
          background: white;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        
        .week-nav-btn:hover {
          border-color: var(--primary-blue);
          background-color: var(--primary-blue);
          color: white;
        }
        
        .week-info {
          text-align: center;
          min-width: 200px;
        }
        
        .week-title {
          font-size: var(--font-size-lg);
          font-weight: 600;
          color: var(--gray-900);
          margin: 0;
        }
        
        .week-dates {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          margin: var(--spacing-1) 0 0 0;
        }
        
        .week-navigation-card {
          padding-top: var(--spacing-4);
        }
        
        .week-summary {
          margin-top: var(--spacing-2);
        }
        
        .summary-title {
          font-size: var(--font-size-lg);
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: var(--spacing-3);
        }
        
        .summary-stats {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: var(--spacing-3);
        }
        
        .summary-stat {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          background-color: var(--gray-50);
          border-radius: var(--radius-lg);
        }
        
        .stat-icon {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        
        .stat-icon.bg-info {
          background-color: #0ea5e9;
        }
        
        .stat-icon.bg-warning {
          background-color: #f59e0b;
        }
        
        .stat-label {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          margin: 0;
        }
        
        .stat-value {
          font-size: var(--font-size-2xl);
          font-weight: 700;
          color: var(--gray-900);
          margin: 0;
        }
        
        .stat-unit {
          font-size: var(--font-size-base);
          font-weight: 500;
          color: var(--gray-600);
          margin-left: var(--spacing-1);
        }
        
        .results-summary {
          margin-bottom: var(--spacing-4);
        }
        
        .table-container {
          overflow-x: auto;
        }

        .th-sortable {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s;
        }

        .th-sortable:hover {
          background-color: var(--gray-100);
        }

        .th-content {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          justify-content: space-between;
        }

        .sort-icon-neutral {
          color: var(--gray-400);
          opacity: 0.5;
        }

        .sort-icon-active {
          color: var(--primary-blue);
        }

        .th-sortable:hover .sort-icon-neutral {
          opacity: 1;
        }
        
        .order-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
        }
        
        .order-icon {
          width: 40px;
          height: 40px;
          background-color: var(--gray-100);
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gray-600);
        }
        
        .customer-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .customer-name {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .delivery-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .price-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .action-buttons {
          display: flex;
          gap: var(--spacing-2);
        }
        
        .action-btn {
          padding: var(--spacing-2);
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        
        .action-btn.view {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.view:hover {
          background-color: var(--secondary-green);
          color: white;
        }
        
        .action-btn.credits {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.credits:hover {
          background-color: #2c5f2d;
          color: white;
        }
        
        .action-btn.edit {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.edit:hover:not(:disabled) {
          background-color: var(--primary-blue);
          color: white;
        }
        
        .action-btn.edit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .action-btn.revert {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.revert:hover {
          background-color: #f59e0b;
          color: white;
        }
        
        .action-btn.delete {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.delete:hover:not(:disabled) {
          background-color: var(--error);
          color: white;
        }
        
        .action-btn.delete:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .empty-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }
        
        .empty-icon {
          font-size: 4rem;
          margin-bottom: var(--spacing-4);
        }
        
        .loading-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .error-message {
          margin-bottom: var(--spacing-4);
        }
        
        .alert {
          padding: var(--spacing-4);
          border-radius: var(--radius-md);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .alert-error {
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--spacing-4);
          }

          .client-type-selector-buttons {
            grid-template-columns: 1fr;
          }
          
          .week-navigation {
            flex-direction: column;
            gap: var(--spacing-4);
          }
          
          .week-controls {
            justify-content: center;
          }
          
          .summary-stats {
            grid-template-columns: repeat(3, 1fr);
            gap: var(--spacing-2);
          }
        }
        
        @media (max-width: 640px) {
          .summary-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: var(--spacing-2);
          }
          
          .table {
            font-size: var(--font-size-sm);
          }
          
          .order-info {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-2);
          }
          
          .order-icon {
            width: 32px;
            height: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default Orders;
